import { makeAutoObservable, runInAction } from "mobx";

import type MainStore from "./MainStore";
import { apiService } from "../services";
import { saveBlob } from "../services/fileDownloadService";
import { SelectOption } from "../components/ui/core/CoreSelect";
import {
  BillingSummary,
  CasesBreakdown,
  ClaimOutcomeTracking,
  FinancialImpact,
  SuccessFeePerClaimRow,
  SuccessFeesPerClaimTotals,
  UsageActivityFeed,
  UsageActivityFeedColumnFilters,
  UsageActivityFeedTotals,
  UsageByAgent,
  UsageKpis,
  DrillDownCapability,
  DrillDownCapabilityLineItem,
  ObservabilityMainFilters,
  DEFAULT_OBSERVABILITY_MAIN_FILTERS,
} from "../types/observability";
import type { ColumnFilterEntry } from "../types/claims";
import type {
  ColumnFilterState,
  ColumnFilterType,
  FilterOperator,
  SortingState,
} from "../types/table";
import {
  CUSTOM_DATE_INTERVAL,
  getDateRangeForInterval,
  normalizeObservabilityDateRange,
  resolveObservabilityDateRange,
} from "../components/helpers/dateIntervalHelper";
import { formatAgentLabel } from "../components/helpers/observabilityHelper";
import {
  loadPersistedObservabilityFilters,
  savePersistedObservabilityFilters,
} from "../components/helpers/observabilityFiltersStorage";
import {
  getUniqueUsageActivityFeedColumnValues,
  getUsageActivityFeedColumnFilterOperator,
  getUsageActivityFeedColumnFilterValues,
} from "../components/helpers/usageActivityFeedColumnFilterHelper";

// The viewer's IANA timezone (e.g. "Europe/Bucharest"), sent with CSV exports
// so the backend renders timestamps in the user's local time.
export const currentTimeZone = (): string | undefined => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || undefined;
  } catch {
    return undefined;
  }
};

export interface CsvDownloadResult {
  success: boolean;
  error?: string;
}

export default class ObservabilityStore {
  mainStore: MainStore | null = null;

  // Loaders
  isUsageByAgentLoading: boolean = false;
  isCasesBreakdownLoading: boolean = false;
  isUsageKpisLoading: boolean = false;
  isClaimOutcomeTrackingLoading: boolean = false;
  isUsageActivityFeedLoading: boolean = false;
  isUsageActivityFeedLoadingMore: boolean = false;
  isFinancialImpactLoading: boolean = false;
  isSuccessFeesPerClaimLoading: boolean = false;
  isDrillDownCapabilityLoading: boolean = false;
  isDrillDownCapabilityLineItemsLoading: boolean = false;
  isOptionListLoading: boolean = false;

  // Data
  usageByAgent: UsageByAgent[] | null = null;
  casesBreakdown: CasesBreakdown | null = null;
  usageKpis: UsageKpis | null = null;
  claimOutcomeTracking: ClaimOutcomeTracking | null = null;
  usageActivityFeed: UsageActivityFeed[] | null = null;
  // Pagination / grand totals for the activity feed (infinite scroll).
  usageActivityFeedTotal: number = 0;
  usageActivityFeedTotals: UsageActivityFeedTotals | null = null;
  usageActivityFeedOffset: number = 0;
  // Server-side sort (the feed is paginated, so sorting only the loaded page
  // would be wrong — the backend sorts the full set, then we page). Default:
  // newest first, matching the backend default.
  usageActivityFeedSort: SortingState = [{ id: "started_at", desc: true }];
  financialImpact: FinancialImpact | null = null;
  // Per-claim success fees (REV-1824) — the dashboard's right-hand table.
  successFeesPerClaim: SuccessFeePerClaimRow[] | null = null;
  successFeesPerClaimTotals: SuccessFeesPerClaimTotals | null = null;
  // The per-claim table filters and sorts its (small, unpaginated) rows
  // in-memory; the state lives here so the CSV download can send the same
  // filters and sort to the backend. Empty sort = backend order.
  successFeesPerClaimColumnFilters: Record<string, ColumnFilterState> = {};
  successFeesPerClaimSort: SortingState = [];
  billingSummary: BillingSummary | null = null;
  drillDownCapability: DrillDownCapability | null = null;
  drillDownCapabilityLineItems: DrillDownCapabilityLineItem | null = null;

  // Filters
  mainFilters: ObservabilityMainFilters = {
    ...DEFAULT_OBSERVABILITY_MAIN_FILTERS,
  };

  // Date filter, column filters and column visibility are persisted together
  // under one localStorage key (see observabilityFiltersStorage) and restored
  // in the constructor / clearState via restorePersistedFilters().
  usageActivityFeedColumnVisibility: Record<string, boolean> = {
    ...ObservabilityStore.defaultUsageActivityFeedColumnVisibility,
  };
  usageActivityFeedColumnPins: Record<string, boolean> =
    this.loadUsageActivityFeedColumnPins();
  usageActivityFeedColumnFilters: UsageActivityFeedColumnFilters | null = null;
  usageActivityFeedRowsForFilterOptions: UsageActivityFeed[] | null = null;
  usageActivityFeedHasBillingPermission: boolean = false;
  // Backend-provided list-filter options (all data, unfiltered), keyed by column id.
  usageActivityFeedOptions: Record<string, SelectOption[]> = {};
  successFeesPerClaimOptions: Record<string, SelectOption[]> = {};

  successFeesPerClaimColumnVisibility: Record<string, boolean> = {
    ...ObservabilityStore.defaultSuccessFeesPerClaimColumnVisibility,
  };
  successFeesPerClaimColumnPins: Record<string, boolean> =
    this.loadSuccessFeesPerClaimColumnPins();

  // Per-loader stale-request guards. Each loader increments its own counter at
  // start (and clearState() bumps all of them). Concurrent batch loads share
  // no counter, so parallel requests in loadUsageMetrics don't invalidate each
  // other.
  private _loadGenerations = {
    usageByAgent: 0,
    casesBreakdown: 0,
    usageKpis: 0,
    claimOutcomeTracking: 0,
    usageActivityFeed: 0,
    financialImpact: 0,
    successFeesPerClaim: 0,
    drillDownCapability: 0,
    drillDownCapabilityLineItems: 0,
    pageOptions: 0,
    usageActivityFeedOptions: 0,
    successFeesPerClaimOptions: 0,
  };

  private beginLoad(loader: keyof typeof this._loadGenerations): number {
    return ++this._loadGenerations[loader];
  }

  private isCurrentLoad(
    loader: keyof typeof this._loadGenerations,
    gen: number,
  ): boolean {
    return gen === this._loadGenerations[loader];
  }

  private invalidateAllLoads(): void {
    for (const loader of Object.keys(
      this._loadGenerations,
    ) as (keyof typeof this._loadGenerations)[]) {
      this._loadGenerations[loader]++;
    }
  }

  private resetLoadingFlags(): void {
    this.isUsageByAgentLoading = false;
    this.isCasesBreakdownLoading = false;
    this.isUsageKpisLoading = false;
    this.isClaimOutcomeTrackingLoading = false;
    this.isUsageActivityFeedLoading = false;
    this.isUsageActivityFeedLoadingMore = false;
    this.isFinancialImpactLoading = false;
    this.isSuccessFeesPerClaimLoading = false;
    this.isDrillDownCapabilityLoading = false;
    this.isDrillDownCapabilityLineItemsLoading = false;
    this.isOptionListLoading = false;
  }

  constructor(mainStore: MainStore) {
    makeAutoObservable(this, {
      mainStore: false,
      _loadGenerations: false,
    } as Record<string, boolean>);
    this.mainStore = mainStore;
    this.restorePersistedFilters();
  }

  //#region getters
  get observabilityDateRange() {
    return resolveObservabilityDateRange(
      this.mainFilters.dateInterval,
      this.mainFilters.customDateFrom,
      this.mainFilters.customDateTo,
    );
  }
  get usageActivityFeedHasMore(): boolean {
    return (this.usageActivityFeed?.length ?? 0) < this.usageActivityFeedTotal;
  }
  get usageActivityFeedSortParams():
    | { sort: string; order: "asc" | "desc" }
    | Record<string, never> {
    const sort = this.usageActivityFeedSort[0];
    if (!sort) return {};
    return { sort: sort.id, order: sort.desc ? "desc" : "asc" };
  }
  get successFeesPerClaimSortParams():
    | { sort: string; order: "asc" | "desc" }
    | Record<string, never> {
    const sort = this.successFeesPerClaimSort[0];
    if (!sort) return {};
    return { sort: sort.id, order: sort.desc ? "desc" : "asc" };
  }
  get hasUsageActivityFeedColumnFiltersApplied(): boolean {
    return (
      this.usageActivityFeedColumnFilters != null &&
      Object.keys(this.usageActivityFeedColumnFilters).length > 0
    );
  }
  get agentTypeOptions(): SelectOption[] {
    const classifications = new Set<string>();

    for (const row of this.usageByAgent ?? []) {
      const classification = row.classification?.trim();
      if (classification) classifications.add(classification);
    }

    return [...classifications]
      .sort((a, b) => formatAgentLabel(a).localeCompare(formatAgentLabel(b)))
      .map((classification) => ({
        value: classification,
        label: formatAgentLabel(classification),
      }));
  }
  //#endregion

  static readonly usageActivityFeedFilterableColumns: readonly string[] = [
    "started_at",
    "finished_at",
    "classification",
    "claim_number",
    "payer",
    "user",
    "billed_amount",
    "paid_amount",
    "cost",
    "has_837",
    "count_835",
    "count_document_uploads",
    "successful_run",
    "chat_id",
  ];

  static readonly usageActivityFeedColumnFilterTypes: Record<
    string,
    ColumnFilterType
  > = {
    claim_number: "text",
    chat_id: "text",
    user: "list",
    payer: "text",
    started_at: "dateRange",
    finished_at: "dateRange",
    has_837: "boolean",
    successful_run: "boolean",
    count_835: "priceRange",
    count_document_uploads: "priceRange",
    billed_amount: "priceRange",
    paid_amount: "priceRange",
    cost: "priceRange",
  };

  private static readonly defaultUsageActivityFeedColumnVisibility: Record<
    string,
    boolean
  > = {
    started_at: true,
    finished_at: false,
    classification: true,
    claim_number: true,
    payer: true,
    user: true,
    billed_amount: true,
    paid_amount: true,
    cost: true,
    has_837: false,
    count_835: false,
    count_document_uploads: false,
    successful_run: false,
    chat_id: false,
  };

  private static mergeUsageActivityFeedColumnVisibility(
    saved: Record<string, boolean> | null,
  ): Record<string, boolean> {
    if (!saved) {
      return { ...ObservabilityStore.defaultUsageActivityFeedColumnVisibility };
    }
    const parsed: Record<string, boolean> = { ...saved };
    // REV-1673: the "agent_type" column was renamed to "classification".
    // Migrate any persisted preference over and drop the dead key so old
    // saved blobs don't carry an orphaned entry.
    if ("agent_type" in parsed) {
      if (!("classification" in parsed)) {
        parsed.classification = parsed.agent_type;
      }
      delete parsed.agent_type;
    }
    return {
      ...ObservabilityStore.defaultUsageActivityFeedColumnVisibility,
      ...parsed,
      classification: true,
    };
  }

  toggleUsageActivityFeedColumnVisibility = (columnId: string) => {
    if (columnId === "classification") return;
    this.usageActivityFeedColumnVisibility = {
      ...this.usageActivityFeedColumnVisibility,
      [columnId]: !this.usageActivityFeedColumnVisibility[columnId],
    };
    this.savePersistedFilters();
  };

  isUsageActivityFeedColumnVisible = (columnId: string): boolean => {
    if (columnId === "classification") return true;
    return this.usageActivityFeedColumnVisibility[columnId] !== false;
  };

  private loadUsageActivityFeedColumnPins(): Record<string, boolean> {
    const STORAGE_KEY = "usageActivityFeedColumnPins";

    if (typeof window !== "undefined") {
      try {
        const saved = localStorage.getItem(STORAGE_KEY);
        if (saved) {
          return JSON.parse(saved);
        }
      } catch {
        // Ignore localStorage errors
      }
    }

    return {};
  }

  private saveUsageActivityFeedColumnPins(): void {
    const STORAGE_KEY = "usageActivityFeedColumnPins";
    try {
      localStorage.setItem(
        STORAGE_KEY,
        JSON.stringify(this.usageActivityFeedColumnPins),
      );
    } catch {
      // Ignore localStorage errors
    }
  }

  // "started_at" is the anchor column: always pinned, and pinning it is a no-op.
  toggleUsageActivityFeedColumnPin = (columnId: string) => {
    if (columnId === "started_at") return;
    const nextPinned = !this.isUsageActivityFeedColumnPinned(columnId);
    this.usageActivityFeedColumnPins = {
      ...this.usageActivityFeedColumnPins,
      [columnId]: nextPinned,
    };
    // Pinning a hidden column also makes it visible so it has a visible effect
    if (nextPinned) {
      this.usageActivityFeedColumnVisibility = {
        ...this.usageActivityFeedColumnVisibility,
        [columnId]: true,
      };
      this.savePersistedFilters();
    }
    this.saveUsageActivityFeedColumnPins();
  };

  isUsageActivityFeedColumnPinned = (columnId: string): boolean => {
    if (columnId === "started_at") return true;
    return !!this.usageActivityFeedColumnPins[columnId];
  };

  private static readonly defaultSuccessFeesPerClaimColumnVisibility: Record<
    string,
    boolean
  > = {
    claim_number: true,
    appeal_date: true,
    payer: true,
    user_displayName: true,
    paid_at_appeal: true,
    paid_now: true,
    payment_date: true,
    success_fee: true,
  };

  private static mergeSuccessFeesPerClaimColumnVisibility(
    saved: Record<string, boolean> | null,
  ): Record<string, boolean> {
    return {
      ...ObservabilityStore.defaultSuccessFeesPerClaimColumnVisibility,
      ...saved,
    };
  }

  // Claim and Success Fee are the load-bearing columns — always visible.
  toggleSuccessFeesPerClaimColumnVisibility = (columnId: string) => {
    if (columnId === "claim_number" || columnId === "success_fee") return;
    this.successFeesPerClaimColumnVisibility = {
      ...this.successFeesPerClaimColumnVisibility,
      [columnId]: !this.successFeesPerClaimColumnVisibility[columnId],
    };
    this.savePersistedFilters();
  };

  isSuccessFeesPerClaimColumnVisible = (columnId: string): boolean => {
    if (columnId === "claim_number" || columnId === "success_fee") return true;
    return this.successFeesPerClaimColumnVisibility[columnId] !== false;
  };

  private loadSuccessFeesPerClaimColumnPins(): Record<string, boolean> {
    const STORAGE_KEY = "successFeesPerClaimColumnPins";

    if (typeof window !== "undefined") {
      try {
        const saved = localStorage.getItem(STORAGE_KEY);
        if (saved) {
          return JSON.parse(saved);
        }
      } catch {
        // Ignore localStorage errors
      }
    }

    return {};
  }

  private saveSuccessFeesPerClaimColumnPins(): void {
    const STORAGE_KEY = "successFeesPerClaimColumnPins";
    try {
      localStorage.setItem(
        STORAGE_KEY,
        JSON.stringify(this.successFeesPerClaimColumnPins),
      );
    } catch {
      // Ignore localStorage errors
    }
  }

  // "claim_number" is the anchor column: always pinned, and pinning it is a no-op.
  toggleSuccessFeesPerClaimColumnPin = (columnId: string) => {
    if (columnId === "claim_number") return;
    const nextPinned = !this.isSuccessFeesPerClaimColumnPinned(columnId);
    this.successFeesPerClaimColumnPins = {
      ...this.successFeesPerClaimColumnPins,
      [columnId]: nextPinned,
    };
    // Pinning a hidden column also makes it visible so it has a visible effect
    if (nextPinned) {
      this.successFeesPerClaimColumnVisibility = {
        ...this.successFeesPerClaimColumnVisibility,
        [columnId]: true,
      };
      this.savePersistedFilters();
    }
    this.saveSuccessFeesPerClaimColumnPins();
  };

  isSuccessFeesPerClaimColumnPinned = (columnId: string): boolean => {
    if (columnId === "claim_number") return true;
    return !!this.successFeesPerClaimColumnPins[columnId];
  };

  getUsageActivityFeedColumnFilterValues = (columnId: string): string[] => {
    const values = getUsageActivityFeedColumnFilterValues(
      this.usageActivityFeedColumnFilters,
      columnId,
    );
    return this.mapUsageActivityFeedFilterValuesForUi(columnId, values);
  };

  getUsageActivityFeedColumnFilterOperator = (
    columnId: string,
  ): FilterOperator =>
    getUsageActivityFeedColumnFilterOperator(
      this.usageActivityFeedColumnFilters,
      columnId,
    );

  getUniqueUsageActivityFeedColumnValues = (columnId: string): string[] => {
    // The user column displays the backend-resolved display name; build options
    // from the feed rows themselves (no username→label mapping needed anymore).
    if (columnId === "user") {
      const seen = new Set<string>();
      for (const row of this.usageActivityFeedRowsForFilterOptions ?? []) {
        const name = row.user_displayName?.trim();
        if (name) seen.add(name);
      }
      return [...seen].sort((a, b) => a.localeCompare(b));
    }

    return getUniqueUsageActivityFeedColumnValues(
      this.usageActivityFeedRowsForFilterOptions,
      columnId,
      this.usageActivityFeedColumnFilters,
    );
  };

  // Display label for a backend option value. Labels shared by several options
  // (duplicate user display names) get the value appended so each entry stays
  // distinguishable. Returns "" when the column has no backend option for the
  // value, letting the filter UI apply its own default formatting.
  getUsageActivityFeedOptionLabel = (columnId: string, value: string): string => {
    const options = this.usageActivityFeedOptions[columnId];
    const option = options?.find((o) => String(o.value) === String(value));
    if (!option) return "";

    const isDuplicate =
      options!.filter((o) => o.label === option.label).length > 1;
    return isDuplicate ? `${option.label} (${option.value})` : option.label;
  };

  private mapUsageActivityFeedFilterValuesForApi = (
    columnId: string,
    values: string[],
  ): string[] => {
    // Backend-option columns are keyed on option values (e.g. user ids) in the
    // UI already, so they go to the API as-is. Mapping from labels would break
    // on duplicate display names (two "John Smith"s share one label).
    if (this.usageActivityFeedOptions[columnId]?.length) return values;

    const rows = this.usageActivityFeedRowsForFilterOptions ?? [];

    // user filter: display name (UI) -> user_id (API), resolved from the rows.
    if (columnId === "user") {
      return values.map((label) => {
        const match = rows.find((row) => row.user_displayName === label);
        return match?.user_id != null ? String(match.user_id) : label;
      });
    }

    if (columnId !== "classification") return values;

    return values.map((value) => {
      const match = rows.find(
        (row) => formatAgentLabel(row.classification) === value,
      );
      return match?.classification ?? value;
    });
  };

  private mapUsageActivityFeedFilterValuesForUi = (
    columnId: string,
    values: string[],
  ): string[] => {
    // Backend-option columns stay keyed on option values; the table renders
    // them through getUsageActivityFeedOptionLabel.
    if (this.usageActivityFeedOptions[columnId]?.length) return values;

    const rows = this.usageActivityFeedRowsForFilterOptions ?? [];

    // user filter: stored user_id -> display name for chips.
    if (columnId === "user") {
      return values.map((id) => {
        const match = rows.find((row) => String(row.user_id) === String(id));
        return match?.user_displayName ?? id;
      });
    }

    if (columnId !== "classification") return values;
    return values.map((value) => formatAgentLabel(value));
  };

  setUsageActivityFeedColumnFilter = (
    columnId: string,
    values: string[],
    operator: FilterOperator = "and",
  ) => {
    const filteredValues = values.filter(Boolean);
    const filterType =
      ObservabilityStore.usageActivityFeedColumnFilterTypes[columnId] ?? "list";

    if (filteredValues.length === 0) {
      this.clearUsageActivityFeedColumnFilter(columnId);
      return;
    }

    if (!this.usageActivityFeedColumnFilters) {
      this.usageActivityFeedColumnFilters = {};
    }

    const apiValues = this.mapUsageActivityFeedFilterValuesForApi(
      columnId,
      filteredValues,
    );

    if (filterType === "dateRange") {
      this.usageActivityFeedColumnFilters = {
        ...this.usageActivityFeedColumnFilters,
        [columnId]: apiValues,
      };
    } else {
      const entry: ColumnFilterEntry = { operator, values: apiValues };
      this.usageActivityFeedColumnFilters = {
        ...this.usageActivityFeedColumnFilters,
        [columnId]: entry,
      };
    }
    this.savePersistedFilters();

    void this.loadUsageActivityFeed(this.usageActivityFeedHasBillingPermission);
  };

  clearUsageActivityFeedColumnFilter = (columnId: string) => {
    if (!this.usageActivityFeedColumnFilters) return;

    const filters = { ...this.usageActivityFeedColumnFilters };
    delete filters[columnId];
    this.usageActivityFeedColumnFilters =
      Object.keys(filters).length > 0 ? filters : null;
    this.savePersistedFilters();

    void this.loadUsageActivityFeed(this.usageActivityFeedHasBillingPermission);
  };

  // Values are stored as the table hands them over (range filters keep their
  // [min, max] positions); the table filters its rows from them in-memory.
  setSuccessFeesPerClaimColumnFilter = (
    columnId: string,
    values: string[],
    operator: FilterOperator = "and",
  ) => {
    this.successFeesPerClaimColumnFilters = {
      ...this.successFeesPerClaimColumnFilters,
      [columnId]: { values, operator },
    };
    this.savePersistedFilters();
  };

  clearSuccessFeesPerClaimColumnFilter = (columnId: string) => {
    const filters = { ...this.successFeesPerClaimColumnFilters };
    delete filters[columnId];
    this.successFeesPerClaimColumnFilters = filters;
    this.savePersistedFilters();
  };

  setSuccessFeesPerClaimSort = (sorting: SortingState) => {
    this.successFeesPerClaimSort = sorting;
    this.savePersistedFilters();
  };

  //#region setters
  setIsUsageByAgentLoading = (isLoading: boolean) => {
    this.isUsageByAgentLoading = isLoading;
  };
  setIsCasesBreakdownLoading = (isLoading: boolean) => {
    this.isCasesBreakdownLoading = isLoading;
  };
  setIsUsageKpisLoading = (isLoading: boolean) => {
    this.isUsageKpisLoading = isLoading;
  };
  setIsClaimOutcomeTrackingLoading = (isLoading: boolean) => {
    this.isClaimOutcomeTrackingLoading = isLoading;
  };
  setIsUsageActivityFeedLoading = (isLoading: boolean) => {
    this.isUsageActivityFeedLoading = isLoading;
  };
  setIsFinancialImpactLoading = (isLoading: boolean) => {
    this.isFinancialImpactLoading = isLoading;
  };
  setIsSuccessFeesPerClaimLoading = (isLoading: boolean) => {
    this.isSuccessFeesPerClaimLoading = isLoading;
  };
  setIsDrillDownCapabilityLoading = (isLoading: boolean) => {
    this.isDrillDownCapabilityLoading = isLoading;
  };
  setIsDrillDownCapabilityLineItemsLoading = (isLoading: boolean) => {
    this.isDrillDownCapabilityLineItemsLoading = isLoading;
  };

  applyObservabilityDateInterval = (dateInterval: string) => {
    this.mainFilters.dateInterval = dateInterval;
    if (dateInterval !== CUSTOM_DATE_INTERVAL) {
      const range = getDateRangeForInterval(dateInterval);
      this.mainFilters.customDateFrom = range.from;
      this.mainFilters.customDateTo = range.to;
    }
    this.savePersistedFilters();
  };

  applyObservabilityCustomDateRange = (from: string, to: string) => {
    const normalized = normalizeObservabilityDateRange(from, to);
    this.mainFilters.dateInterval = CUSTOM_DATE_INTERVAL;
    this.mainFilters.customDateFrom = normalized.from;
    this.mainFilters.customDateTo = normalized.to;
    this.savePersistedFilters();
  };

  ensureObservabilityDateDefaults = () => {
    const { customDateFrom, customDateTo, dateInterval } = this.mainFilters;
    if (customDateFrom && customDateTo) {
      return;
    }
    const range = getDateRangeForInterval(
      dateInterval === CUSTOM_DATE_INTERVAL ? "this_month" : dateInterval,
    );
    this.mainFilters.customDateFrom = range.from;
    this.mainFilters.customDateTo = range.to;
    if (dateInterval === CUSTOM_DATE_INTERVAL) {
      this.mainFilters.dateInterval = "this_month";
    }
  };

  restorePersistedFilters = () => {
    const persisted = loadPersistedObservabilityFilters(this.mainStore?.filtersScope ?? null);
    this.mainFilters = persisted.mainFilters;
    this.usageActivityFeedColumnFilters =
      persisted.usageActivityFeed.columnFilters;
    this.usageActivityFeedColumnVisibility =
      ObservabilityStore.mergeUsageActivityFeedColumnVisibility(
        persisted.usageActivityFeed.columnVisibility,
      );
    this.successFeesPerClaimColumnFilters =
      persisted.successFeesPerClaim.columnFilters;
    this.successFeesPerClaimColumnVisibility =
      ObservabilityStore.mergeSuccessFeesPerClaimColumnVisibility(
        persisted.successFeesPerClaim.columnVisibility,
      );
    // An empty saved feed sort falls back to the default, like setUsageActivityFeedSort.
    const feedSort = persisted.usageActivityFeed.sort;
    this.usageActivityFeedSort =
      feedSort && feedSort.length > 0
        ? feedSort
        : [{ id: "started_at", desc: true }];
    this.successFeesPerClaimSort = persisted.successFeesPerClaim.sort ?? [];
    this.ensureObservabilityDateDefaults();
  };

  private savePersistedFilters(): void {
    savePersistedObservabilityFilters(this.mainStore?.filtersScope ?? null, {
      mainFilters: { ...this.mainFilters },
      usageActivityFeed: {
        columnFilters: this.usageActivityFeedColumnFilters,
        columnVisibility: this.usageActivityFeedColumnVisibility,
        sort: this.usageActivityFeedSort,
      },
      successFeesPerClaim: {
        columnFilters: this.successFeesPerClaimColumnFilters,
        columnVisibility: this.successFeesPerClaimColumnVisibility,
        sort: this.successFeesPerClaimSort,
      },
    });
  }

  // Persisted filters are re-read from storage rather than reset, so the
  // dashboard looks the same when the user comes back.
  clearState = () => {
    this.invalidateAllLoads();
    this.resetLoadingFlags();
    this.usageByAgent = null;
    this.casesBreakdown = null;
    this.usageKpis = null;
    this.claimOutcomeTracking = null;
    this.usageActivityFeed = null;
    this.usageActivityFeedTotal = 0;
    this.usageActivityFeedTotals = null;
    this.usageActivityFeedOffset = 0;

    this.financialImpact = null;
    this.successFeesPerClaim = null;
    this.successFeesPerClaimTotals = null;
    this.billingSummary = null;
    this.drillDownCapability = null;
    this.drillDownCapabilityLineItems = null;

    this.restorePersistedFilters();
    this.usageActivityFeedColumnPins = this.loadUsageActivityFeedColumnPins();
    this.usageActivityFeedRowsForFilterOptions = null;
    this.usageActivityFeedHasBillingPermission = false;
    this.usageActivityFeedOptions = {};
    this.successFeesPerClaimOptions = {};
    this.successFeesPerClaimColumnPins =
      this.loadSuccessFeesPerClaimColumnPins();
  };
  //#endregion

  //#region API Calls
  loadUsageMetrics = (hasBillingPermission: boolean) => {
    this.loadUsageByAgent();
    this.loadCasesBreakdown();
    this.loadUsageKpis();
    this.loadUsageActivityFeed(hasBillingPermission);
  };

  loadBillingMetrics = async () => {
    this.loadFinancialImpact();
    this.loadSuccessFeesPerClaim();
  };

  loadUsageByAgent = async () => {
    const gen = this.beginLoad("usageByAgent");
    try {
      if (this.isCurrentLoad("usageByAgent", gen))
        this.setIsUsageByAgentLoading(true);

      const response = await apiService.getUsageByAgent(
        this.observabilityDateRange,
      );
      if (response.success && this.isCurrentLoad("usageByAgent", gen)) {
        runInAction(() => {
          this.usageByAgent = response.data?.rows || [];
        });
      }

      if (this.isCurrentLoad("usageByAgent", gen))
        this.setIsUsageByAgentLoading(false);
    } catch (error) {
      if (this.isCurrentLoad("usageByAgent", gen))
        this.setIsUsageByAgentLoading(false);
    }
  };

  loadCasesBreakdown = async () => {
    const gen = this.beginLoad("casesBreakdown");
    try {
      if (this.isCurrentLoad("casesBreakdown", gen))
        this.setIsCasesBreakdownLoading(true);

      const response = await apiService.getUsageCasesBreakdown(
        this.observabilityDateRange,
      );
      if (response.success && this.isCurrentLoad("casesBreakdown", gen)) {
        runInAction(() => {
          this.casesBreakdown = response.data || null;
        });
      }

      if (this.isCurrentLoad("casesBreakdown", gen))
        this.setIsCasesBreakdownLoading(false);
    } catch (error) {
      if (this.isCurrentLoad("casesBreakdown", gen))
        this.setIsCasesBreakdownLoading(false);
    }
  };

  loadUsageKpis = async () => {
    const gen = this.beginLoad("usageKpis");
    try {
      if (this.isCurrentLoad("usageKpis", gen))
        this.setIsUsageKpisLoading(true);

      const response = await apiService.getUsageKpis({
        ...this.observabilityDateRange,
      });
      if (response.success && this.isCurrentLoad("usageKpis", gen)) {
        runInAction(() => {
          this.usageKpis = response.data || null;
        });
      }

      if (this.isCurrentLoad("usageKpis", gen))
        this.setIsUsageKpisLoading(false);
    } catch (error) {
      if (this.isCurrentLoad("usageKpis", gen))
        this.setIsUsageKpisLoading(false);
    }
  };

  loadClaimOutcomeTracking = async () => {
    const gen = this.beginLoad("claimOutcomeTracking");
    try {
      if (this.isCurrentLoad("claimOutcomeTracking", gen))
        this.setIsClaimOutcomeTrackingLoading(true);

      const response = await apiService.getClaimOutcomeTracking({
        ...this.observabilityDateRange,
      });
      if (response.success && this.isCurrentLoad("claimOutcomeTracking", gen)) {
        runInAction(() => {
          this.claimOutcomeTracking = response.data || null;
        });
      }

      if (this.isCurrentLoad("claimOutcomeTracking", gen))
        this.setIsClaimOutcomeTrackingLoading(false);
    } catch (error) {
      if (this.isCurrentLoad("claimOutcomeTracking", gen))
        this.setIsClaimOutcomeTrackingLoading(false);
    }
  };

  static readonly USAGE_ACTIVITY_FEED_PAGE_SIZE = 50;

  loadUsageActivityFeed = async (hasBillingPermission: boolean) => {
    const gen = this.beginLoad("usageActivityFeed");
    try {
      if (this.isCurrentLoad("usageActivityFeed", gen)) {
        this.usageActivityFeedHasBillingPermission = hasBillingPermission;
        this.setIsUsageActivityFeedLoading(true);
        this.isUsageActivityFeedLoadingMore = false;
      }

      // The backend feed row is now fully self-contained — one row per chat,
      // with cost/recovery and the user's display name (user_displayName)
      // served directly. No client-side merge and no username→label mapping.
      // `total` and `totals` are grand values over the FULL filtered set (not
      // just this page) so the footer reconciles regardless of scroll position.
      const response = await apiService.getUsageActivityFeed({
        ...this.observabilityDateRange,
        limit: ObservabilityStore.USAGE_ACTIVITY_FEED_PAGE_SIZE,
        offset: 0,
        ...this.usageActivityFeedSortParams,
        columnFilters: this.usageActivityFeedColumnFilters,
      });

      if (response.success && this.isCurrentLoad("usageActivityFeed", gen)) {
        runInAction(() => {
          this.usageActivityFeed = response.data?.rows || [];
          this.usageActivityFeedRowsForFilterOptions = this.usageActivityFeed;
          this.usageActivityFeedTotal = response.data?.total ?? 0;
          this.usageActivityFeedTotals = response.data?.totals ?? null;
          this.usageActivityFeedOffset = response.data?.rows?.length ?? 0;
        });
      }

      if (this.isCurrentLoad("usageActivityFeed", gen))
        this.setIsUsageActivityFeedLoading(false);
    } catch (error) {
      if (this.isCurrentLoad("usageActivityFeed", gen))
        this.setIsUsageActivityFeedLoading(false);
    }
  };

  // CSV export of the whole filtered feed, rendered by the backend so it covers
  // every row — not just the pages loaded so far. `columns` (visible table
  // column ids, in display order) limits the file to those columns.
  downloadUsageActivityFeedCsv = async (
    columns?: string[],
  ): Promise<CsvDownloadResult> => {
    const response = await apiService.downloadUsageActivityFeedCsv({
      ...this.observabilityDateRange,
      ...this.usageActivityFeedSortParams,
      columnFilters: this.usageActivityFeedColumnFilters,
      columns,
      tz: currentTimeZone(),
    });

    if (!response.success || !response.data) {
      return {
        success: false,
        error: response.error || "Failed to download the activity feed CSV",
      };
    }

    saveBlob(
      response.data.blob,
      response.data.filename || "usage-activity-feed.csv",
    );
    return { success: true };
  };

  // Append the next page on scroll (infinite scroll). No-op while a load is in
  // flight or when everything is already loaded.
  loadMoreUsageActivityFeed = async () => {
    if (
      this.isUsageActivityFeedLoading ||
      this.isUsageActivityFeedLoadingMore ||
      !this.usageActivityFeedHasMore
    ) {
      return;
    }
    const gen = this.beginLoad("usageActivityFeed");
    try {
      if (this.isCurrentLoad("usageActivityFeed", gen)) {
        runInAction(() => {
          this.isUsageActivityFeedLoadingMore = true;
        });
      }

      const response = await apiService.getUsageActivityFeed({
        ...this.observabilityDateRange,
        limit: ObservabilityStore.USAGE_ACTIVITY_FEED_PAGE_SIZE,
        offset: this.usageActivityFeedOffset,
        ...this.usageActivityFeedSortParams,
        columnFilters: this.usageActivityFeedColumnFilters,
      });

      if (response.success && this.isCurrentLoad("usageActivityFeed", gen)) {
        runInAction(() => {
          const incoming = response.data?.rows || [];
          this.usageActivityFeed = [
            ...(this.usageActivityFeed ?? []),
            ...incoming,
          ];
          this.usageActivityFeedRowsForFilterOptions = this.usageActivityFeed;
          this.usageActivityFeedTotal =
            response.data?.total ?? this.usageActivityFeedTotal;
          this.usageActivityFeedTotals =
            response.data?.totals ?? this.usageActivityFeedTotals;
          this.usageActivityFeedOffset += incoming.length;
        });
      }

      if (this.isCurrentLoad("usageActivityFeed", gen)) {
        runInAction(() => {
          this.isUsageActivityFeedLoadingMore = false;
        });
      }
    } catch (error) {
      if (this.isCurrentLoad("usageActivityFeed", gen)) {
        runInAction(() => {
          this.isUsageActivityFeedLoadingMore = false;
        });
      }
    }
  };

  // Sort change resets to page 0 and reloads from the server (sorting the full
  // set, not just the loaded page). An empty array restores the default order.
  setUsageActivityFeedSort = (sorting: SortingState) => {
    this.usageActivityFeedSort =
      sorting.length > 0 ? sorting : [{ id: "started_at", desc: true }];
    this.savePersistedFilters();
    void this.loadUsageActivityFeed(this.usageActivityFeedHasBillingPermission);
  };

  loadPageOptions = async () => {
    const gen = this.beginLoad("pageOptions");
    this.isOptionListLoading = true;
    try {
      await Promise.all([
        this.loadUsageActivityFeedOptions(),
        this.loadSuccessFeesPerClaimOptions(),
      ]);
    } finally {
      if (this.isCurrentLoad("pageOptions", gen)) {
        runInAction(() => {
          this.isOptionListLoading = false;
        });
      }
    }
  };

  loadUsageActivityFeedOptions = async () => {
    const gen = this.beginLoad("usageActivityFeedOptions");
    try {
      const response = await apiService.getUsageActivityFeedOptions();
      // A failed response clears the list too: keeping the previous one would
      // offer filter options that no longer describe the data.
      if (this.isCurrentLoad("usageActivityFeedOptions", gen)) {
        runInAction(() => {
          this.usageActivityFeedOptions = response.success ? (response.data ?? {}) : {};
        });
      }
    } catch (error) {
      if (this.isCurrentLoad("usageActivityFeedOptions", gen)) {
        runInAction(() => {
          this.usageActivityFeedOptions = {};
        });
      }
    }
  };

  loadFinancialImpact = async () => {
    const gen = this.beginLoad("financialImpact");
    try {
      if (this.isCurrentLoad("financialImpact", gen))
        this.setIsFinancialImpactLoading(true);

      const response = await apiService.getFinancialImpact(
        this.observabilityDateRange,
      );
      if (response.success && this.isCurrentLoad("financialImpact", gen)) {
        runInAction(() => {
          this.financialImpact = response.data || null;
        });
      }

      if (this.isCurrentLoad("financialImpact", gen))
        this.setIsFinancialImpactLoading(false);
    } catch (error) {
      if (this.isCurrentLoad("financialImpact", gen))
        this.setIsFinancialImpactLoading(false);
    }
  };

  // Per-claim success fees (REV-1824): one row per claim, computed once on the
  // claim's canonical recovery chain — the feed stays per-case, this table is
  // the per-claim truth for contingency fees.
  loadSuccessFeesPerClaim = async () => {
    const gen = this.beginLoad("successFeesPerClaim");
    try {
      if (this.isCurrentLoad("successFeesPerClaim", gen))
        this.setIsSuccessFeesPerClaimLoading(true);

      const response = await apiService.getSuccessFeesPerClaim(
        this.observabilityDateRange,
      );
      if (response.success && this.isCurrentLoad("successFeesPerClaim", gen)) {
        runInAction(() => {
          this.successFeesPerClaim = response.data?.rows || [];
          this.successFeesPerClaimTotals = response.data?.totals ?? null;
        });
      }

      if (this.isCurrentLoad("successFeesPerClaim", gen))
        this.setIsSuccessFeesPerClaimLoading(false);
    } catch (error) {
      if (this.isCurrentLoad("successFeesPerClaim", gen))
        this.setIsSuccessFeesPerClaimLoading(false);
    }
  };

  // CSV export of the per-claim success fee report, rendered by the backend
  // with the table's current column filters and sort. `columns` (visible table
  // column ids, in display order) limits the file to those columns.
  downloadSuccessFeesPerClaimCsv = async (
    columns?: string[],
  ): Promise<CsvDownloadResult> => {
    const response = await apiService.downloadSuccessFeesPerClaimCsv({
      ...this.observabilityDateRange,
      ...this.successFeesPerClaimSortParams,
      columnFilters: this.successFeesPerClaimColumnFilters,
      columns,
      tz: currentTimeZone(),
    });

    if (!response.success || !response.data) {
      return {
        success: false,
        error: response.error || "Failed to download the success fees CSV",
      };
    }

    saveBlob(
      response.data.blob,
      response.data.filename || "success-fees-per-claim.csv",
    );
    return { success: true };
  };

  loadSuccessFeesPerClaimOptions = async () => {
    const gen = this.beginLoad("successFeesPerClaimOptions");
    try {
      const response = await apiService.getSuccessFeesPerClaimOptions();
      // A failed response clears the list too: keeping the previous one would
      // offer filter options that no longer describe the data.
      if (this.isCurrentLoad("successFeesPerClaimOptions", gen)) {
        runInAction(() => {
          this.successFeesPerClaimOptions = response.success ? (response.data ?? {}) : {};
        });
      }
    } catch (error) {
      if (this.isCurrentLoad("successFeesPerClaimOptions", gen)) {
        runInAction(() => {
          this.successFeesPerClaimOptions = {};
        });
      }
    }
  };

  loadDrillDownCapability = async () => {
    const gen = this.beginLoad("drillDownCapability");
    try {
      if (this.isCurrentLoad("drillDownCapability", gen))
        this.setIsDrillDownCapabilityLoading(true);

      const response = await apiService.getDrillDownCapabilityCompute(
        this.observabilityDateRange,
      );

      if (response.success && this.isCurrentLoad("drillDownCapability", gen)) {
        runInAction(() => {
          this.drillDownCapability = response.data || null;
        });
      }

      if (this.isCurrentLoad("drillDownCapability", gen))
        this.setIsDrillDownCapabilityLoading(false);
    } catch (error) {
      if (this.isCurrentLoad("drillDownCapability", gen))
        this.setIsDrillDownCapabilityLoading(false);
    }
  };

  loadDrillDownCapabilityLineItems = async () => {
    const gen = this.beginLoad("drillDownCapabilityLineItems");
    try {
      if (this.isCurrentLoad("drillDownCapabilityLineItems", gen))
        this.setIsDrillDownCapabilityLineItemsLoading(true);

      const response = await apiService.getDrillDownCapabilityLineItems(
        this.observabilityDateRange,
      );
      if (
        response.success &&
        this.isCurrentLoad("drillDownCapabilityLineItems", gen)
      ) {
        runInAction(() => {
          if (!response.data) {
            this.drillDownCapabilityLineItems = null;
            return;
          }

          this.drillDownCapabilityLineItems = {
            rows: response.data.rows,
            window: this.drillDownCapability?.window ?? {
              from: this.observabilityDateRange.from,
              to: this.observabilityDateRange.to,
              is_current_open_cycle: false,
            },
          };
        });
      }

      if (this.isCurrentLoad("drillDownCapabilityLineItems", gen))
        this.setIsDrillDownCapabilityLineItemsLoading(false);
    } catch (error) {
      if (this.isCurrentLoad("drillDownCapabilityLineItems", gen))
        this.setIsDrillDownCapabilityLineItemsLoading(false);
    }
  };
  //#endregion
}
